import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import MatrixRain from "../components/MatrixRain";
import HealthIndicator from "../components/HealthIndicator";
import NotificationBell from "../components/NotificationBell/NotificationBell";
import { usePermissions } from "../hooks";

export default function OperatorLayout({
  currentPath,
  onNavigate,
  onLogout,
  onToggleRain,
  showRain = true,
  topBadge = "",
  autoFtp = false,
  ftpConfigured = false,
  onToggleAutoFtp,
  user = null,
  children,
}) {
  const { canAccessEdit } = usePermissions();
  const { t } = useTranslation();

  // Build tabs with permission-based visibility
  const tabs = useMemo(() => {
    const allTabs = [
      { key: "/overview", label: t('nav.overview'), access: true },
      { key: "/edit", label: t('nav.edit'), access: canAccessEdit },
      { key: "/matches", label: t('nav.matches'), access: true },
      { key: "/manage", label: t('nav.manage'), access: true },
      { key: "/statistics", label: t('nav.statistics'), access: true },
    ];
    return allTabs.filter((tab) => tab.access);
  }, [canAccessEdit, t]);

  return (
    <div className="app-shell">
      {showRain && <MatrixRain opacity={0.18} speed={0.5} />}
      <header className="topbar">
        <div className="brand" onClick={() => onNavigate("/overview")}>
          <span className="brand-mark">{t('topbar.brand')}</span>
          <span className="brand-sub">{t('topbar.brandSub')}</span>
          <span className={`brand-subtle ${showRain ? "matrix-glow" : ""}`}>{t('topbar.brandSubtle')}</span>
        </div>
        <nav className="nav-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`tab ${currentPath === tab.key ? "active" : ""}`}
              onClick={() => onNavigate(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </nav>
        <div className="top-actions">
          <HealthIndicator onNavigate={onNavigate} />
          <NotificationBell />
          {topBadge ? <div className="rate-pill">{topBadge}</div> : null}
          {user && (
            <button
              className={`ghost user-welcome ${currentPath === "/me" ? "active" : ""}`}
              onClick={() => onNavigate("/me")}
              title={t('topbar.accountSettings')}
            >
              {user.username}
            </button>
          )}
          {/* Auto-FTP toggle - temporary feature */}
          {ftpConfigured && (
            <button
              className={`ghost ${autoFtp ? "ftp-active" : ""}`}
              onClick={onToggleAutoFtp}
              title={autoFtp ? t('topbar.ftpEnabledTooltip') : t('topbar.ftpDisabledTooltip')}
            >
              {autoFtp ? t('topbar.ftpOn') : t('topbar.ftpOff')}
            </button>
          )}
          <button className="ghost" onClick={onToggleRain}>
            {showRain ? t('topbar.effectsOn') : t('topbar.effectsOff')}
          </button>
          <button className="ghost" onClick={onLogout}>
            {t('topbar.logout')}
          </button>
        </div>
      </header>
      <main className="app-main">{children}</main>
    </div>
  );
}
